import { useState } from 'react';
import useSync from 'hooks/useSync';

import CommonButton from 'components/CommonButton/CommonButton';
import axiosInstance from 'apis/axiosInstance';
import SyncStatus from '../../SyncStatus/SynStatus';
import { SYNC_STATUS, ROLE } from 'utils/constants';
import LatestSyncMessage from '../LatestSyncMessage/LatestSyncMessage';
import { renderToast } from 'components/Toast/Toast';
import { useUser } from 'context/userContext';
import getIsSyncDisabled from 'pages/SyncPage/util/getIsSyncDisabled';
import InfoTooltip from 'pages/SyncPage/InfoTooltip/InfoTooltip';

const SyncCacheSection = () => {
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const { user } = useUser();

  const { data, isLoading, mutate } = useSync();

  const isSyncDisabled = getIsSyncDisabled(
    data?.cache,
    user.adminRole?.role === ROLE.GLOBAL_ADMIN
  );

  const syncCache = async () => {
    setIsButtonLoading(true);
    try {
      const response = await axiosInstance.get('/sync/caches');
      const numberOfCaches = response.data.data.length;
      const isNewCachesObtained = numberOfCaches > 0;

      renderToast({
        message: isNewCachesObtained
          ? `Successfully obtained caches`
          : response.data.message,
        type: isNewCachesObtained ? 'success' : 'info',
      });
    } catch (error) {
      renderToast({
        message: 'An error has occurred. Please try again.',
        type: 'error',
      });
    }
    await mutate();
    setIsButtonLoading(false);
  };

  return (
    <div className="py-2 border-b border-light-grey">
      <div className="flex justify-between">
        <div className="basis-[45rem]">
          <div className="flex gap-x-0.9">
            <h3 className="font-bold">
              <p>Sync Cache Data</p>
              <p>
                Execute this step last, after completing the practice setup.
              </p>
            </h3>

            <InfoTooltip id={'cache'} display={isSyncDisabled} />
          </div>
          <p className="mt-2">
            Click the Update button to load and cache your booking data for the
            next 7 months. This will improve loading speed throughout the
            FirstIn application.
          </p>
        </div>
        <div className="flex flex-col items-end">
          <CommonButton
            variant="secondary"
            className="!min-h-[3.2rem]"
            onClick={syncCache}
            isLoading={isButtonLoading}
            disabled={
              data?.cache?.status === SYNC_STATUS.IN_PROGRESS ||
              isLoading ||
              isSyncDisabled
            }
          >
            Update
          </CommonButton>
        </div>
      </div>
      <div className="flex flex-col gap-y-0.8 mt-2">
        <SyncStatus
          isLoading={isLoading}
          data={{
            ...data?.cache,
            status: isButtonLoading
              ? SYNC_STATUS.IN_PROGRESS
              : data?.cache?.status,
          }}
        />
        <LatestSyncMessage
          isLoading={isLoading}
          lastSyncAt={data?.cache?.lastSyncAt}
          lastSuccessSyncAt={data?.cache?.lastSuccessSyncAt}
          isUpdateMessage
        />
      </div>
    </div>
  );
};

export default SyncCacheSection;
